import type { Endomorphism } from '@code-expert/prelude';
import { identity, number, pipe } from '@code-expert/prelude';
import type { RequiredProps } from '@code-expert/type-utils';
import type { ResultFormat } from '/imports/domain';
import { resultFormatAdt } from '/imports/domain';
import type { PercentOptions } from './number';
import { formatPercentWithOptions, makeNumberFormat, replaceMinus } from './number';

export type Result = number | null | undefined;

type RenderRatio = (n: string, d: string) => string;

export const renderRatioCompact: RenderRatio = (n, d) => `${n}/${d}`;
export const renderRatioThinSpace: RenderRatio = (n, d) => `${n}\u2009/\u2009${d}`;
export const renderRatioOf: RenderRatio = (n, d) => `${n} of ${d}`;

interface RatioOptions {
  renderRatio?: RenderRatio;
}

type RenderPoints = Endomorphism<string>;

export const renderPointsNoSuffix: RenderPoints = identity;
export const renderPointsShortSuffix: RenderPoints = (p) => `${p}\u2009P`;
export const renderPointsLongSuffix: RenderPoints = (p) => `${p} points`;

export interface ResultFormatOptions extends PercentOptions, RatioOptions {
  fallback?: string;
  showMaximumPoints?: false;
  renderPoints?: RenderPoints;
}

const defaultFallback = 'N/A';

const defaultOptions = {
  fallback: defaultFallback,
  renderRatio: renderRatioThinSpace,
  renderPoints: renderPointsShortSuffix,
} satisfies ResultFormatOptions;

const round = (n: number, precision: number) => {
  const factor = 1 / precision;
  return Math.round(n * factor) / factor;
};

const formatRatio = (p: string | number, max: number, options: RatioOptions = {}) => {
  const { renderRatio = renderRatioThinSpace } = options;
  return renderRatio(p.toString(), max.toString());
};

const withNumericResult = (
  result: Result,
  render: (result: number) => string,
  fallback = defaultFallback,
): string => (result == null || result > 1 || result < 0 ? fallback : render(result));

const withMaximumPoints = (
  maxPoints: number | undefined,
  f: (maxPoints: number) => string,
): string => (maxPoints == null || maxPoints === 0 ? '' : f(maxPoints));

/**
 * Turns a unit interval (result in [0, 1]) into a ratio (e.g. "3/5").
 * Useful for displaying a result to TAs.
 * For student-facing code, consider {@link formatResultAsPercent}
 * @see {@link formatPercentAsRatio}
 */
const formatResultAsRatio = (
  maximumPoints: number,
  precision = 1,
  options?: ResultFormatOptions,
) => {
  const { fallback } = { ...defaultOptions, ...options };
  return (result: Result) =>
    withNumericResult(
      result,
      (r) => formatRatio(round(r * maximumPoints, precision), Math.round(maximumPoints), options),
      fallback,
    );
};

/**
 * Turns a percentage (value in [0, 100]) into a ratio (e.g. "3/5").
 * @see {@link formatResultAsRatio}
 */
export const formatPercentAsRatio =
  (maximumPoints: number | undefined) =>
  (result: number): string =>
    withMaximumPoints(maximumPoints, (maxPoints) =>
      pipe(result, number.unitIntervalFromRange(0, 100), formatResultAsRatio(maxPoints)),
    );

/**
 * Turns a unit interval (result in [0, 1]) into a percentage (e.g. "60%").
 * Useful for displaying a result to students.
 * For TA-facing code, consider {@link formatResultAsRatio}
 */
const formatResultAsPercent = (result: Result, options: ResultFormatOptions) =>
  number.isFinite(result)
    ? formatPercentWithOptions(options)(result)
    : (options.fallback ?? defaultFallback);

//--------------------------------------------------------------------------------------------------

export type FormatTaskResult = (result: Result) => string;

export type FormatTaskResultRange = (range: [Result, Result]) => string;

export interface TaskResultFormatter {
  withOptions: (options: ResultFormatOptions) => TaskResultFormatter;
  formatResult: FormatTaskResult;
  formatResultRange: FormatTaskResultRange;
}

//--------------------------------------------------------------------------------------------------

export type FormatResult = (result: Result, maxPoints: number | undefined) => string;

export type FormatResultRange = (range: [Result, Result], maxPoints: number | undefined) => string;

export interface ResultFormatter {
  withOptions: (options: ResultFormatOptions) => ResultFormatter;
  withMaxPoints: (maxPoints: number | undefined) => TaskResultFormatter;
  formatResult: FormatResult;
  formatResultRange: FormatResultRange;
}

const withMaxPoints = (
  formatter: ResultFormatter,
  maxPoints: number | undefined,
): TaskResultFormatter => ({
  withOptions: (options) => withMaxPoints(formatter.withOptions(options), maxPoints),
  formatResult: (result) => formatter.formatResult(result, maxPoints),
  formatResultRange: (range) => formatter.formatResultRange(range, maxPoints),
});

const mkResultFormatter = (
  resultFormat: ResultFormat,
  options: RequiredProps<ResultFormatOptions, keyof typeof defaultOptions>,
): ResultFormatter => ({
  withOptions: (opts) => mkResultFormatter(resultFormat, { ...options, ...opts }),

  withMaxPoints: (maxPoints) => withMaxPoints(mkResultFormatter(resultFormat, options), maxPoints),

  formatResult: (result, maximumPoints) => {
    const { fallback, showMaximumPoints, renderPoints } = options;
    return resultFormatAdt.fold(resultFormat, {
      percent: () => formatResultAsPercent(result, options),
      points: ({ precision }) =>
        withMaximumPoints(maximumPoints, (maxPoints) => {
          const formatted =
            showMaximumPoints === false
              ? withNumericResult(
                  result,
                  (r) => round(r * maxPoints, precision).toString(),
                  fallback,
                )
              : formatResultAsRatio(maxPoints, precision, options)(result);
          return formatted === fallback ? formatted : renderPoints(formatted);
        }),
    });
  },

  formatResultRange: ([min, max], maximumPoints) => {
    const { fallback, renderPoints } = options;
    return min == null && max == null
      ? fallback
      : resultFormatAdt.fold(resultFormat, {
          percent: () => {
            const fmt = (r: Result) => formatResultAsPercent(r, options);
            return `${fmt(min)}–${fmt(max)}`;
          },
          points: ({ precision }) =>
            withMaximumPoints(maximumPoints, (maxPoints) => {
              const fmt = (r: Result) => (r == null ? fallback : round(r * maxPoints, precision));
              return renderPoints(
                formatRatio(`${fmt(min)}–${fmt(max)}`, Math.round(maxPoints), options),
              );
            }),
        });
  },
});

export const makeResultFormatter = (resultFormat: ResultFormat): ResultFormatter =>
  mkResultFormatter(resultFormat, defaultOptions);

export const defaultResultFormatter = makeResultFormatter(resultFormatAdt.percent());

export const defaultTaskResultFormatter = defaultResultFormatter.withMaxPoints(undefined);

//--------------------------------------------------------------------------------------------------

const renderPercentAsDecimal = (percentage: number): string => {
  const fmt = makeNumberFormat({ maximumFractionDigits: percentage < 1 ? 4 : 0 });
  return `${fmt.format(percentage)}%`;
};

/**
 * Formats a unit interval (result in [0, 1]) as percentage (e.g. "60%").
 * In contrast to {@link formatResultAsPercent}, 99.9 is not rounded to 99.
 * Percentages smaller than 1 are formatted with decimal places.
 */
export const formatResultDifferenceAsPercent = (result: number, fallback = defaultFallback) =>
  number.isFinite(result)
    ? pipe(result, number.percentFromUnitInterval, renderPercentAsDecimal, replaceMinus)
    : fallback;
